/**
 * The expression tree the calculus tools share.
 *
 * Numbers are exact rationals (`n / d`) wherever they can be, so that 1/3 − 1 is −2/3
 * and not −0.6666666666666667, and so that a derivative such as d/dx x^(1/3) reads
 * (1/3)·x^(−2/3). A number that is not exactly representable (a long decimal, or an
 * overflow) falls back to a float, which is a `num` with `d === 1` and a non-integer `n`.
 *
 * Subtraction and division are not node kinds: `a − b` is `a + (−1)·b` and `a / b` is
 * `a · b^(−1)`. Sums and products are n-ary and flat. The printer turns them back into
 * the forms a reader expects.
 */

export type FnName =
  | "sin"
  | "cos"
  | "tan"
  | "sec"
  | "csc"
  | "cot"
  | "asin"
  | "acos"
  | "atan"
  | "sinh"
  | "cosh"
  | "tanh"
  | "ln"
  | "log"
  | "abs";

export type ConstName = "pi" | "e";

export type Node =
  | { kind: "num"; n: number; d: number }
  | { kind: "const"; name: ConstName }
  | { kind: "var"; name: string }
  | { kind: "add"; terms: Node[] }
  | { kind: "mul"; factors: Node[] }
  | { kind: "pow"; base: Node; exp: Node }
  | { kind: "call"; fn: FnName; arg: Node };

export type NumNode = Extract<Node, { kind: "num" }>;

/** Rationals are kept while numerator and denominator stay below this. */
const EXACT_LIMIT = 2 ** 40;

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** A number node n/d in lowest terms; a float when the fraction is not exact. */
export function num(n: number, d = 1): NumNode {
  if (!(Number.isFinite(n) && Number.isFinite(d)) || d === 0) {
    return { kind: "num", n: Number.NaN, d: 1 };
  }
  if (Number.isInteger(n) && Number.isInteger(d)) {
    const g = gcd(n, d) || 1;
    const sign = d < 0 ? -1 : 1;
    const nn = (sign * n) / g;
    const dd = (sign * d) / g;
    if (Math.abs(nn) < EXACT_LIMIT && dd < EXACT_LIMIT) {
      return { kind: "num", n: nn === 0 ? 0 : nn, d: dd };
    }
  }
  return { kind: "num", n: n / d, d: 1 };
}

export const ZERO: NumNode = num(0);
export const ONE: NumNode = num(1);
export const MINUS_ONE: NumNode = num(-1);
export const TWO: NumNode = num(2);
export const HALF: NumNode = num(1, 2);

export function constant(name: ConstName): Node {
  return { kind: "const", name };
}

export function variable(name: string): Node {
  return { kind: "var", name };
}

export function add(...terms: Node[]): Node {
  return { kind: "add", terms };
}

export function mul(...factors: Node[]): Node {
  return { kind: "mul", factors };
}

export function pow(base: Node, exp: Node): Node {
  return { kind: "pow", base, exp };
}

export function call(fn: FnName, arg: Node): Node {
  return { kind: "call", fn, arg };
}

export function neg(node: Node): Node {
  return mul(MINUS_ONE, node);
}

export function sub(a: Node, b: Node): Node {
  return add(a, neg(b));
}

export function div(a: Node, b: Node): Node {
  return mul(a, pow(b, MINUS_ONE));
}

export function sqrtOf(node: Node): Node {
  return pow(node, HALF);
}

/* ------------------------------------------------------------ numbers */

export function isNum(node: Node): node is NumNode {
  return node.kind === "num";
}

export function isExactNum(node: Node): node is NumNode {
  return (
    node.kind === "num" && Number.isInteger(node.n) && Number.isInteger(node.d)
  );
}

export function isIntegerNum(node: Node): node is NumNode {
  return node.kind === "num" && node.d === 1 && Number.isInteger(node.n);
}

export function numValue(node: NumNode): number {
  return node.n / node.d;
}

export function isNumEqual(node: Node, value: number): boolean {
  return node.kind === "num" && node.n / node.d === value;
}

export function addNum(a: NumNode, b: NumNode): NumNode {
  if (isExactNum(a) && isExactNum(b)) {
    return num(a.n * b.d + b.n * a.d, a.d * b.d);
  }
  return num(numValue(a) + numValue(b));
}

export function mulNum(a: NumNode, b: NumNode): NumNode {
  if (isExactNum(a) && isExactNum(b)) {
    return num(a.n * b.n, a.d * b.d);
  }
  return num(numValue(a) * numValue(b));
}

export function negNum(a: NumNode): NumNode {
  return num(-a.n, a.d);
}

/** a^k for an exact rational a and integer k; null when it would not stay exact. */
export function powNumInteger(a: NumNode, k: number): NumNode | null {
  if (!(isExactNum(a) && Number.isInteger(k)) || Math.abs(k) > 1024) {
    return null;
  }
  if (a.n === 0) {
    return k > 0 ? ZERO : null;
  }
  let n = 1;
  let d = 1;
  for (let i = 0; i < Math.abs(k); i += 1) {
    n *= a.n;
    d *= a.d;
    if (Math.abs(n) >= EXACT_LIMIT || d >= EXACT_LIMIT) {
      return null;
    }
  }
  return k >= 0 ? num(n, d) : num(d, n);
}

/* ---------------------------------------------------------- structure */

/** A canonical string: equal for trees that differ only in the order of sums/products. */
export function keyOf(node: Node): string {
  switch (node.kind) {
    case "num":
      return Number.isInteger(node.n) && Number.isInteger(node.d)
        ? `${node.n}/${node.d}`
        : `f${node.n}`;
    case "const":
      return node.name;
    case "var":
      return `$${node.name}`;
    case "add":
      return `+(${node.terms.map(keyOf).sort().join(",")})`;
    case "mul":
      return `*(${node.factors.map(keyOf).sort().join(",")})`;
    case "pow":
      return `^(${keyOf(node.base)},${keyOf(node.exp)})`;
    default:
      return `${node.fn}(${keyOf(node.arg)})`;
  }
}

export function equals(a: Node, b: Node): boolean {
  return keyOf(a) === keyOf(b);
}

export function children(node: Node): Node[] {
  switch (node.kind) {
    case "add":
      return node.terms;
    case "mul":
      return node.factors;
    case "pow":
      return [node.base, node.exp];
    case "call":
      return [node.arg];
    default:
      return [];
  }
}

export function dependsOn(node: Node, name: string): boolean {
  if (node.kind === "var") {
    return node.name === name;
  }
  return children(node).some((child) => dependsOn(child, name));
}

/** Variable names in order of first appearance. */
export function variablesOf(node: Node, found: string[] = []): string[] {
  if (node.kind === "var" && !found.includes(node.name)) {
    found.push(node.name);
  }
  for (const child of children(node)) {
    variablesOf(child, found);
  }
  return found;
}

export function nodeCount(node: Node): number {
  let total = 1;
  for (const child of children(node)) {
    total += nodeCount(child);
  }
  return total;
}

/** Replace every occurrence of a variable. */
export function substitute(node: Node, name: string, value: Node): Node {
  switch (node.kind) {
    case "var":
      return node.name === name ? value : node;
    case "add":
      return add(...node.terms.map((t) => substitute(t, name, value)));
    case "mul":
      return mul(...node.factors.map((f) => substitute(f, name, value)));
    case "pow":
      return pow(
        substitute(node.base, name, value),
        substitute(node.exp, name, value)
      );
    case "call":
      return call(node.fn, substitute(node.arg, name, value));
    default:
      return node;
  }
}
